import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import { getIssuanceDetailApi, acceptIssuanceApi, rejectIssuanceApi, reviewRejectionApi } from 'api/services/uniformManagement';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge, rejectionReasonOptions } from '../../constants';
import FilterSelect from 'components/Form/filterSelect';
import { Modal, Switch, Radio, Alert } from 'antd';
import { useUniformMgmt } from '../../context/uniformMgmtContext';

const IssuanceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { warehouseOptions, warehousesLoading } = useUniformMgmt();

  const [issuance, setIssuance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState(null);
  const [rejectDetail, setRejectDetail] = useState('');

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewAction, setReviewAction] = useState('APPROVED');
  const [returnWarehouse, setReturnWarehouse] = useState(null);
  const [creditStock, setCreditStock] = useState(true);
  const [reviewLoading, setReviewLoading] = useState(false);

  const fetchData = () => {
    setLoading(true);
    getIssuanceDetailApi(id).then(response => {
      setIssuance(response.data?.data || null);
      setLoading(false);
    }).catch(() => { setLoading(false); navigate('/uniform-management/issuance'); });
  };

  useEffect(() => { fetchData(); }, [id]);

  const handleAccept = () => {
    setActionLoading(true);
    acceptIssuanceApi(id).then(() => {
      toast.success('Issuance accepted', { position: "top-right", autoClose: 3000, transition: Bounce });
      setActionLoading(false);
      fetchData();
    }).catch(e => {
      setActionLoading(false);
      toast.error(e.response?.data?.detail || 'Failed to accept', { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const openReview = () => {
    setReviewAction('APPROVED');
    setReturnWarehouse(null);
    setCreditStock(true);
    setShowReviewModal(true);
  };

  const handleReview = () => {
    const rejectionId = issuance?.rejection?.id;
    if (!rejectionId) {
      toast.error('No rejection record found', { position: "top-right", autoClose: 3000, transition: Bounce });
      return;
    }
    setReviewLoading(true);
    reviewRejectionApi(rejectionId, {
      review_status: reviewAction,
      return_to_warehouse: reviewAction === 'APPROVED' && returnWarehouse ? returnWarehouse.value : null,
      credit_stock: reviewAction === 'APPROVED' ? creditStock : true,
    }).then(() => {
      toast.success(`Rejection ${reviewAction.toLowerCase()}`, { position: "top-right", autoClose: 3000, transition: Bounce });
      setReviewLoading(false);
      setShowReviewModal(false);
      fetchData();
    }).catch(e => {
      setReviewLoading(false);
      const data = e.response?.data;
      const msg = data?.error || data?.detail || data?.non_field_errors?.[0] || 'Review failed';
      toast.error(msg, { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const handleReject = () => {
    if (!rejectReason) {
      toast.error('Please select a reason', { position: "top-right", autoClose: 3000, transition: Bounce });
      return;
    }
    setActionLoading(true);
    rejectIssuanceApi(id, {
      reason: rejectReason.value,
      reason_detail: rejectDetail
    }).then(() => {
      toast.success('Issuance rejected', { position: "top-right", autoClose: 3000, transition: Bounce });
      setActionLoading(false);
      setShowRejectModal(false);
      fetchData();
    }).catch(e => {
      setActionLoading(false);
      toast.error(e.response?.data?.detail || 'Failed to reject', { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  if (loading || !issuance) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="text-center py-5"><i className="fa fa-spinner fa-spin fa-2x" /></div>
        </div>
      </div>
    );
  }

  const rejection = issuance.rejection;

  const Field = ({ label, children }) => (
    <div className="mb-3">
      <div className="text-muted" style={{ fontSize: 12, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, color: '#1F1F1F', fontWeight: 500 }}>{children || '—'}</div>
    </div>
  );

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
          <div className="d-flex align-items-center gap-3">
<div>
              <div className="text-muted" style={{ fontSize: 12 }}>Issuance</div>
              <h3 className="mb-0" style={{ fontSize: 22, fontWeight: 700 }}>#{issuance.id}</h3>
            </div>
          </div>
          <span style={{ ...getStatusBadge(issuance.status), padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
            {issuance.status}
          </span>
        </div>

        <div className="row g-3">
          <div className="col-lg-8">
            <div className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0" style={{ fontSize: 15 }}>Issuance Details</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <Field label="Employee">
                      <div>{issuance.company_user_name}</div>
                      {issuance.company_user_emp_no && (
                        <small className="text-muted fw-normal">{issuance.company_user_emp_no}</small>
                      )}
                    </Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Uniform Item">{issuance.uniform_item_name}</Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Size">
                      {issuance.size_detail ? `${issuance.size_detail.name} (${issuance.size_detail.code})` : null}
                    </Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Quantity">{issuance.quantity}</Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Warehouse">{issuance.warehouse_name}</Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Issuance Date">{formatDateHandler(issuance.issuance_date)}</Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Issued By">{issuance.issued_by_name}</Field>
                  </div>
                  <div className="col-md-6">
                    <Field label="Next Replacement">
                      {issuance.next_replacement_date ? formatDateHandler(issuance.next_replacement_date) : null}
                    </Field>
                  </div>
                  {issuance.notes && (
                    <div className="col-12">
                      <Field label="Notes">{issuance.notes}</Field>
                    </div>
                  )}
                </div>

                {issuance.status === 'ISSUED' && (
                  <div className="d-flex gap-2 mt-2 pt-3" style={{ borderTop: '1px solid #F1F2F4' }}>
                    <Button label="Accept" icon="fa fa-check" className="btn btn-success"
                      loading={actionLoading} onClick={handleAccept} />
                    <Button label="Reject" icon="fa fa-times" className="btn btn-outline-danger"
                      loading={actionLoading} onClick={() => setShowRejectModal(true)} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="mb-0" style={{ fontSize: 15 }}>Item</h5>
              </div>
              <div className="card-body d-flex flex-column align-items-center justify-content-center">
                {issuance.uniform_item_image ? (
                  <img src={issuance.uniform_item_image} alt={issuance.uniform_item_name}
                    style={{
                      maxWidth: '100%', maxHeight: 220, objectFit: 'contain',
                      borderRadius: 10, background: '#F9FAFB', padding: 12
                    }} />
                ) : (
                  <div style={{
                    width: '100%', minHeight: 200, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    background: '#F9FAFB', borderRadius: 10, color: '#9CA3AF', fontSize: 36,
                  }}>
                    <i className="fa fa-tshirt" />
                  </div>
                )}
                <div className="mt-3 text-center">
                  <div className="fw-semibold" style={{ fontSize: 14 }}>{issuance.uniform_item_name}</div>
                  {issuance.size_detail && (
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      Size {issuance.size_detail.code}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Rejection Info */}
        {rejection && (
          <div className="card mt-3" style={{ borderColor: '#FECACA' }}>
            <div className="card-header d-flex align-items-center justify-content-between"
              style={{ background: '#FEF2F2', borderBottomColor: '#FECACA' }}>
              <h5 className="mb-0 d-flex align-items-center gap-2" style={{ fontSize: 15, color: '#B91C1C' }}>
                <i className="fa fa-exclamation-triangle" /> Rejection Details
              </h5>
              <div className="d-flex align-items-center gap-2">
                {rejection.review_status && (
                  <span style={{
                    ...getStatusBadge(
                      rejection.review_status === 'PENDING' ? 'PENDING'
                        : rejection.review_status === 'APPROVED' ? 'ACCEPTED' : 'REJECTED'
                    ),
                    padding: '4px 12px', borderRadius: 999, fontSize: 11, fontWeight: 600
                  }}>
                    {rejection.review_status}
                  </span>
                )}
                {(!rejection.review_status || rejection.review_status === 'PENDING') && (
                  <Button
                    label="Review"
                    icon="fa fa-gavel"
                    className="btn btn-sm btn-primary"
                    onClick={openReview}
                  />
                )}
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <Field label="Reason">
                    {rejectionReasonOptions.find(r => r.value === rejection.reason)?.label || rejection.reason}
                  </Field>
                </div>
                <div className="col-md-4">
                  <Field label="Detail">{rejection.reason_detail}</Field>
                </div>
                <div className="col-md-4">
                  <Field label="Rejected At">{formatDateHandler(rejection.rejected_at)}</Field>
                </div>
                {rejection.reviewed_by && (
                  <div className="col-md-4">
                    <Field label="Reviewed By">
                      <div>{rejection.reviewed_by_name || `User #${rejection.reviewed_by}`}</div>
                      {rejection.reviewed_by_emp_no && (
                        <small className="text-muted fw-normal">{rejection.reviewed_by_emp_no}</small>
                      )}
                    </Field>
                  </div>
                )}
                {rejection.reviewed_at && (
                  <div className="col-md-4">
                    <Field label="Reviewed At">{formatDateHandler(rejection.reviewed_at)}</Field>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Reject Modal */}
        <Modal
          title="Reject Issuance"
          open={showRejectModal}
          onCancel={() => setShowRejectModal(false)}
          width={460}
          centered
          footer={[
            <button key="cancel" type="button" className="btn btn-light"
              onClick={() => setShowRejectModal(false)} disabled={actionLoading}>
              Cancel
            </button>,
            <Button key="submit" label="Reject" className="btn btn-danger ms-2"
              loading={actionLoading} onClick={handleReject} />
          ]}
        >
          <div className="mb-3">
            <label className="col-form-label">Reason <span className="text-danger">*</span></label>
            <FilterSelect isClearable onChange={setRejectReason} optionLabel="label" optionValue="value"
              options={rejectionReasonOptions} value={rejectReason} placeholder="Select Reason" isLoading={false} />
          </div>
          <div className="mb-1">
            <label className="col-form-label">Detail <small className="text-muted">(optional)</small></label>
            <textarea className="form-control" rows={3} value={rejectDetail}
              onChange={(e) => setRejectDetail(e.target.value)} placeholder="Additional details..." />
          </div>
        </Modal>

        {/* Review Rejection Modal */}
        <Modal
          title="Review Rejection"
          open={showReviewModal}
          onCancel={() => setShowReviewModal(false)}
          width={520}
          centered
          destroyOnClose
          footer={[
            <button key="cancel" type="button" className="btn btn-light"
              onClick={() => setShowReviewModal(false)} disabled={reviewLoading}>
              Cancel
            </button>,
            <Button
              key="submit"
              label={reviewAction === 'APPROVED' ? 'Approve Rejection' : 'Deny Rejection'}
              className={`btn ${reviewAction === 'APPROVED' ? 'btn-success' : 'btn-danger'} ms-2`}
              loading={reviewLoading}
              onClick={handleReview}
            />
          ]}
        >
          <div>
            <div className="p-3 mb-3" style={{
              background: '#F9FAFB', borderRadius: 10, border: '1px solid #F1F2F4'
            }}>
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted" style={{ fontSize: 12 }}>Employee</span>
                <span className="fw-semibold">{issuance.company_user_name}</span>
              </div>
              <div className="d-flex justify-content-between mb-1">
                <span className="text-muted" style={{ fontSize: 12 }}>Uniform</span>
                <span className="fw-semibold">{issuance.uniform_item_name}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted" style={{ fontSize: 12 }}>Size</span>
                <span className="fw-semibold">{issuance.size_detail?.code || '—'}</span>
              </div>
            </div>

            <div className="mb-3">
              <label className="col-form-label">Decision</label>
              <Radio.Group
                value={reviewAction}
                onChange={(e) => setReviewAction(e.target.value)}
                optionType="button"
                buttonStyle="solid"
                style={{ width: '100%' }}
              >
                <Radio.Button value="APPROVED" style={{ width: '50%', textAlign: 'center' }}>
                  <i className="fa fa-check me-1" />Approve
                </Radio.Button>
                <Radio.Button value="DENIED" style={{ width: '50%', textAlign: 'center' }}>
                  <i className="fa fa-times me-1" />Deny
                </Radio.Button>
              </Radio.Group>
            </div>

            {reviewAction === 'APPROVED' && (
              <>
                <div className="mb-3 d-flex align-items-center justify-content-between p-3"
                  style={{ background: '#F9FAFB', borderRadius: 10 }}>
                  <div>
                    <div className="fw-semibold" style={{ fontSize: 14 }}>Credit stock back?</div>
                    <small className="text-muted">
                      {creditStock ? 'Stock will be returned to warehouse' : 'Item is damaged or lost'}
                    </small>
                  </div>
                  <Switch checked={creditStock} onChange={setCreditStock} />
                </div>
                {creditStock && (
                  <div className="mb-2">
                    <label className="col-form-label">
                      Return to Warehouse <small className="text-muted">(optional override)</small>
                    </label>
                    <FilterSelect isClearable onChange={setReturnWarehouse} optionLabel="label" optionValue="value"
                      options={warehouseOptions} value={returnWarehouse}
                      placeholder="Use original warehouse" isLoading={warehousesLoading} />
                  </div>
                )}
              </>
            )}

            {reviewAction === 'DENIED' && (
              <Alert
                type="warning"
                showIcon
                message="The issuance status will revert to Accepted and the employee will be notified."
                style={{ borderRadius: 10 }}
              />
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default IssuanceDetail;
